import BaseAuthProvider from './BaseAuthProvider'
import { AuthProviders } from '../constants/authProviders'

/**
 * JWT Authentication Provider
 * For auto-login via JWT token (e.g., /autologin?jwt=xxx or /login?jwt=xxx)
 * Used when user clicks magic link or comes from external system
 * Both routes use the same HomePage component with automatic JWT detection
 */
export class JwtAuthProvider extends BaseAuthProvider {
    getMethod() {
        return AuthProviders.JWT
    }

    /**
     * JWT provider is enabled when:
     * 1. JWT is in .env providers (VUE_APP_AUTH_PROVIDERS)
     * 2. ?jwt=xxx parameter is present in URL
     * This allows auto-prioritization of JWT auth when the parameter is detected
     *
     * @param {Object} context - Context with queryParams and envProviders
     * @returns {boolean} True if enabled in .env AND ?jwt=xxx is present in URL
     */
    isEnabled(context) {
        // 1. Check .env (base requirement)
        if (!context.envProviders.includes(this.getMethod())) {
            return false
        }

        // 2. Check if ?jwt parameter exists in query
        return !!(context.queryParams && context.queryParams.jwt)
    }

    /**
     * Authenticate with JWT token
     * JWT is already a valid token, no conversion needed
     * @param {Object} credentials
     * @param {string} credentials.token - JWT token
     * @returns {Promise<{token: string, tokenType: string}>}
     */
    async authenticate({ token }) {
        if (!token) {
            throw new Error('JWT token is required for JWT authentication')
        }

        // JWT is already valid, return as Bearer token
        return {
            token: token,
            tokenType: 'Bearer'
        }
    }

    /**
     * No alternatives for JWT auto-login
     * It's a one-time direct login method
     */
    getAlternatives(enabledProviders) {
        return []
    }
}

export default JwtAuthProvider
